(() => {
  'use strict';

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const safeZone = { x: 58, y: 48, width: 484, height: 264, right: 542, bottom: 312 };

  const init = (root) => {
    if (!root || root.dataset.ufSmart603Ready === 'true') return;
    const designer = root.querySelector('[data-uf-designer]');
    const panel = designer?.querySelector('[data-uf-smart-designer]');
    const svg = designer?.querySelector('[data-uf-canvas-svg]');
    if (!designer || !panel || !svg) return;
    root.dataset.ufSmart603Ready = 'true';

    const scoreEl = panel.querySelector('[data-uf-smart-score]');
    const meter = panel.querySelector('[data-uf-smart-meter]');
    const summary = panel.querySelector('[data-uf-smart-summary]');
    const issuesEl = panel.querySelector('[data-uf-smart-issues]');
    const materialSelect = panel.querySelector('[data-uf-smart-material]');
    const useCaseSelect = panel.querySelector('[data-uf-smart-use-case]');
    const materialTip = panel.querySelector('[data-uf-smart-material-tip]');
    const analyzeButton = panel.querySelector('[data-uf-smart-analyze]');
    const improveButton = panel.querySelector('[data-uf-smart-improve]');
    const centerButton = panel.querySelector('[data-uf-smart-center]');
    const fitButton = panel.querySelector('[data-uf-smart-fit]');
    const textInput = root.querySelector('[data-uf-engraving-text]');
    let lastResult = null;
    let scheduled = 0;
    let analyzeTimer = 0;
    let destroyed = false;

    const materialTips = {
      wood: 'Wood and bamboo usually engrave best with bold, open shapes and moderate spacing. Very thin script strokes may lose detail.',
      slate: 'Slate gives strong contrast. Keep small text comfortably spaced and avoid overly fine artwork.',
      acrylic: 'Acrylic benefits from clean vector-style artwork and strong outlines. Avoid dense shaded images.',
      glass: 'Glass engraving can soften fine details. Use larger lettering, simple icons, and generous spacing.',
      metal: 'Coated metal works well with crisp monochrome artwork. Keep QR codes large and leave a clear quiet zone.',
      leather: 'Leather can spread fine details slightly. Favor thicker lettering and uncomplicated artwork.'
    };

    const getVisibleObjects = () => {
      const result = [];
      svg.querySelectorAll('[data-uf-canvas-object]').forEach((node) => {
        if (node.hidden || node.getAttribute('aria-hidden') === 'true') return;
        const child = node.querySelector('image, text, path, circle, ellipse, polygon, polyline, rect, line');
        if (!child || child.hidden || child.getAttribute('display') === 'none') return;
        try {
          const box = node.getBBox();
          const transform = node.getCTM();
          if (!box.width || !box.height || !transform) return;
          const points = [
            new DOMPoint(box.x, box.y), new DOMPoint(box.x + box.width, box.y),
            new DOMPoint(box.x, box.y + box.height), new DOMPoint(box.x + box.width, box.y + box.height)
          ].map((point) => point.matrixTransform(transform));
          const xs = points.map((p) => p.x); const ys = points.map((p) => p.y);
          result.push({
            id: node.dataset.ufCanvasObject,
            label: node.dataset.ufObjectLabel || 'Layer',
            type: String(node.dataset.ufCanvasObject || '').split('-copy-')[0].replace(/^icon-.*/, 'icon'),
            x: Math.min(...xs), y: Math.min(...ys), right: Math.max(...xs), bottom: Math.max(...ys),
            width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys)
          });
        } catch (_error) {}
      });
      return result;
    };

    const overlapRatio = (a, b) => {
      const width = Math.max(0, Math.min(a.right, b.right) - Math.max(a.x, b.x));
      const height = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.y, b.y));
      const area = width * height;
      return area / Math.max(1, Math.min(a.width * a.height, b.width * b.height));
    };

    const analyze = () => {
      if (destroyed || !root.isConnected || document.hidden) return lastResult;
      const objects = getVisibleObjects();
      const issues = [];
      let score = 100;
      const text = String(textInput?.value || '').trim();
      const material = materialSelect?.value || 'wood';
      const useCase = useCaseSelect?.value || 'general';

      if (!objects.length && !text) {
        score = 0;
        issues.push({ pass: false, text: 'Add text, artwork, a symbol, or a QR code before analyzing.' });
      }

      objects.forEach((item) => {
        const overflow = Math.max(0, safeZone.x - item.x) + Math.max(0, item.right - safeZone.right) + Math.max(0, safeZone.y - item.y) + Math.max(0, item.bottom - safeZone.bottom);
        if (overflow > 0) { score -= Math.min(25, 8 + overflow / 8); issues.push({ pass: false, text: `${item.label} extends outside the recommended engraving zone.` }); }
        if (item.width < 18 || item.height < 12) { score -= 8; issues.push({ pass: false, text: `${item.label} may be too small to engrave clearly.` }); }
      });

      for (let i = 0; i < objects.length; i += 1) {
        for (let j = i + 1; j < objects.length; j += 1) {
          const ratio = overlapRatio(objects[i], objects[j]);
          if (ratio > 0.22) { score -= ratio > 0.5 ? 18 : 10; issues.push({ pass: false, text: `${objects[i].label} overlaps ${objects[j].label}.` }); }
        }
      }

      const qr = objects.find((item) => item.type === 'qr');
      if (qr && (qr.width < 90 || qr.height < 90)) { score -= 15; issues.push({ pass: false, text: 'The QR code may be too small for reliable scanning after engraving.' }); }
      if (useCase === 'business' && !qr) { score -= 10; issues.push({ pass: false, text: 'Business layouts usually benefit from a QR code or clear contact detail.' }); }

      const lines = text ? text.split(/\r?\n/).filter(Boolean) : [];
      if (lines.length > Number(designer.dataset.maxLines || 4)) { score -= 18; issues.push({ pass: false, text: 'The text has more lines than recommended for this product.' }); }
      if (text.length > 90) { score -= 10; issues.push({ pass: false, text: 'Long text may be difficult to read at engraving size.' }); }
      if ((material === 'glass' || material === 'leather') && text.length > 60) { score -= 8; issues.push({ pass: false, text: 'This material is easier to read with shorter, bolder wording.' }); }

      if (objects.length) {
        const left = Math.min(...objects.map((o) => o.x)); const right = Math.max(...objects.map((o) => o.right));
        const top = Math.min(...objects.map((o) => o.y)); const bottom = Math.max(...objects.map((o) => o.bottom));
        const dx = Math.abs((left + right) / 2 - 300); const dy = Math.abs((top + bottom) / 2 - 180);
        if (dx > 48 || dy > 48) { score -= 10; issues.push({ pass: false, text: 'The overall design looks off-center. Auto improve can rebalance it.' }); }
        else issues.push({ pass: true, text: 'The overall layout is reasonably balanced.' });
      }

      if (!issues.some((item) => !item.pass) && objects.length) issues.push({ pass: true, text: 'All visible layers fit comfortably inside the safe zone.' });
      score = Math.round(clamp(score, 0, 100));
      const grade = score >= 90 ? 'excellent' : score >= 75 ? 'good' : score >= 55 ? 'warning' : 'poor';
      const label = score >= 90 ? 'Production ready' : score >= 75 ? 'Good design' : score >= 55 ? 'Needs adjustment' : 'Major changes needed';
      lastResult = { score, grade, label, issues };
      renderResult(lastResult);
      return lastResult;
    };

    const renderResult = (result) => {
      scoreEl.textContent = `${result.score}`;
      scoreEl.dataset.grade = result.grade;
      meter.style.width = `${result.score}%`;
      summary.textContent = `${result.label} — ${result.score}/100`;
      issuesEl.innerHTML = '';
      result.issues.slice(0, 6).forEach((issue) => {
        const item = document.createElement('li');
        item.className = issue.pass ? 'is-pass' : '';
        item.textContent = issue.text;
        issuesEl.appendChild(item);
      });
      materialTip.textContent = materialTips[materialSelect?.value || 'wood'];
    };

    const command = (name) => designer.dispatchEvent(new CustomEvent('uf:smart-command', { detail: { command: name } }));
    const scheduleAnalyze = () => {
      if (destroyed || document.hidden) return;
      window.clearTimeout(analyzeTimer);
      window.cancelAnimationFrame(scheduled);
      analyzeTimer = window.setTimeout(() => {
        scheduled = window.requestAnimationFrame(() => {
          try { analyze(); } catch (error) { console.warn('[UNIQ Smart Designer] Analysis skipped:', error); }
        });
      }, 120);
    };

    analyzeButton?.addEventListener('click', analyze);
    improveButton?.addEventListener('click', () => { command('auto-improve'); window.setTimeout(analyze, 50); });
    centerButton?.addEventListener('click', () => { command('center-selected'); window.setTimeout(analyze, 40); });
    fitButton?.addEventListener('click', () => { command('fit-selected'); window.setTimeout(analyze, 40); });
    materialSelect?.addEventListener('change', analyze);
    useCaseSelect?.addEventListener('change', analyze);
    designer.addEventListener('uf:canvas-updated', scheduleAnalyze);
    textInput?.addEventListener('input', scheduleAnalyze);
    root.querySelector('[data-uf-artwork-input]')?.addEventListener('change', () => window.setTimeout(analyze, 80));

    materialTip.textContent = materialTips[materialSelect?.value || 'wood'];
    window.setTimeout(() => { try { analyze(); } catch (error) { console.warn('[UNIQ Smart Designer] Initial analysis skipped:', error); } }, 150);

    const onVisibility = () => { if (!document.hidden) scheduleAnalyze(); };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(root)) return;
      destroyed = true;
      window.clearTimeout(analyzeTimer);
      window.cancelAnimationFrame(scheduled);
      document.removeEventListener('visibilitychange', onVisibility);
    }, { once: true });
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-uf-personalization-side]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true }); else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
